import { memoizeClassMethods, Record } from '@gitbook/util-immutable';

import {
    CHILD_OBJECT_INVALID,
    CHILD_REQUIRED,
    CHILD_TYPE_INVALID,
    CHILD_UNKNOWN,
    FIRST_CHILD_OBJECT_INVALID,
    FIRST_CHILD_TYPE_INVALID,
    FRAGMENT_TYPE_INVALID,
    FRAGMENT_TYPE_MISSING,
    LAST_CHILD_OBJECT_INVALID,
    LAST_CHILD_TYPE_INVALID,
    MODEL_TYPES,
    NODE_DATA_INVALID,
    NODE_FRAGMENT_TYPE_INVALID,
    NODE_IS_VOID_INVALID,
    NODE_MARK_INVALID,
    NODE_MISSING_FRAGMENT,
    NODE_TEXT_INVALID,
    NODE_UNKNOWN_FRAGMENT,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    type SchemaViolation,
} from '../constants';
import { Block } from './block';
import type { Change } from './change';
import type { Document } from './document';
import type { Fragment } from './fragment';
import { Inline } from './inline';
import type { KeyedNodeType, NodeType } from './node-serialization';
import { Text } from './text';

// Callback when running a normalization
export type SchemaNormalizeFn = (change: Change) => void;

export type ValidateNodeFn = (node: KeyedNodeType) => null | undefined | SchemaNormalizeFn;

type AllowedChildObjects = 'text' | 'inline' | 'block' | void;
type AllowedParentObjects = AllowedChildObjects | 'document' | 'fragment';

// Rule for a node (used for first, parent, etc)
interface NodeSchemaRule<AllowedObjects> {
    /** Types that should be allowed as a parent */
    types?: Array<string | void>;
    /** Object kinds that should be allowed as a child */
    objects?: Array<AllowedObjects>;
}

// Rule for a list of nodes
type NodesSchemaRule = Array<
    | NodeSchemaRule<AllowedChildObjects>
    | {
          min?: number;
          max?: number;
      }
>;

type FragmentKeys =
    /** Array of fragment keys that should exists */
    | string[]
    /** Mapping of keys to expected types (or undefined) */
    | {
          [key: string]: string | undefined;
      };
type FragmentKeysFn = (block: Block) => FragmentKeys | undefined;

// Rule for a block/document/inline
export interface SchemaRule {
    isVoid?: boolean;
    text?: RegExp;
    /** Validation for marks */
    marks?: Array<{
        type: string;
    }>;
    /** Validation for the data of the node */
    data?: {
        [key: string]: (value: any) => boolean;
    };
    /** Validation for the parent */
    parent?: NodeSchemaRule<AllowedParentObjects>;
    /** Validation for the first child node */
    first?: NodeSchemaRule<AllowedParentObjects>;
    /** Validation for the last child node */
    last?: NodeSchemaRule<AllowedParentObjects>;
    /** Validation for inner nodes */
    nodes?: NodesSchemaRule;
    /** Validation for fragments */
    fragments?: {
        keys?: FragmentKeys | FragmentKeysFn;
        nodes?: NodesSchemaRule;
        /** Map of fragment type to its nodes validation rules
        If the fragment type maps to an empty Array, we fallback to using the fragments.nodes rules **/
        types?: {
            [type: string]: {
                min?: number;
                max?: number;
                nodes?: NodesSchemaRule;
            };
        };
    };
    normalize?: SchemaNormalizeViolationFn<NodeType>;
}
export interface SchemaRulesSet {
    [key: string]: SchemaRule;
}

/**
 * Common properties for all potential context values.
 */
interface SchemaNormalizeCommonContext<NodeT extends NodeType> {
    /** Node being normalized */
    node: NodeT;
    /** Schema rule being applied */
    rule: SchemaRule;
}

export interface SchemaNormalizeContexts<NodeT extends NodeType> {
    [CHILD_OBJECT_INVALID]: SchemaNormalizeCommonContext<NodeT> & {
        child: Block | Inline | Text;
        index: number;
    };
    [CHILD_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT> & {
        child: Block | Inline | Text;
        index: number;
    };
    [CHILD_REQUIRED]: SchemaNormalizeCommonContext<NodeT>;
    [CHILD_UNKNOWN]: SchemaNormalizeCommonContext<NodeT>;
    [FIRST_CHILD_OBJECT_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [FIRST_CHILD_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [FRAGMENT_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [FRAGMENT_TYPE_MISSING]: SchemaNormalizeCommonContext<NodeT>;
    [LAST_CHILD_OBJECT_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [LAST_CHILD_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_DATA_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_FRAGMENT_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_IS_VOID_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_MARK_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_MISSING_FRAGMENT]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_TEXT_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [NODE_UNKNOWN_FRAGMENT]: SchemaNormalizeCommonContext<NodeT>;
    [PARENT_OBJECT_INVALID]: SchemaNormalizeCommonContext<NodeT>;
    [PARENT_TYPE_INVALID]: SchemaNormalizeCommonContext<NodeT> & {
        parent: Block | Inline | Document | Fragment;
    };
}

export type SchemaNormalizeContext<T extends NodeType = NodeType> =
    SchemaNormalizeContexts<T>[keyof SchemaNormalizeContexts<T>];

export type SchemaNormalizeContextForViolation<
    NodeT extends NodeType,
    T extends keyof SchemaNormalizeContexts<NodeT>,
> = SchemaNormalizeContexts<NodeT>[T];

export type SchemaNormalizeViolationFn<
    NodeT extends NodeType,
    Violation extends keyof SchemaNormalizeContexts<NodeT> = keyof SchemaNormalizeContexts<NodeT>,
> = (
    change: Change,
    violation: Violation,
    context: SchemaNormalizeContextForViolation<NodeT, Violation>
) => void;

/*
 * Immutable model to represent a schema.
 */
export class Schema extends Record<{
    validations: ValidateNodeFn[];
    document: SchemaRule;
    blocks: { [type: string]: SchemaRule };
    inlines: { [type: string]: SchemaRule };
}>({
    validations: [],
    document: {},
    blocks: {},
    inlines: {},
}) {
    /*
     * Object.
     */
    get object(): 'schema' {
        return 'schema';
    }

    /*
     * Create a new `Schema` with a set of rules
     */
    public static create(
        attrs:
            | Schema
            | {
                  validations?: ValidateNodeFn[];
                  document?: SchemaRule;
                  blocks?: { [type: string]: SchemaRule };
                  inlines?: { [type: string]: SchemaRule };
              } = {}
    ): Schema {
        if (Schema.isSchema(attrs)) {
            return attrs;
        }

        return Schema.createRecord(attrs);
    }

    /*
     * Check if the input is a `Schema`.
     */
    static isSchema(input: unknown): input is Schema {
        return !!input?.[MODEL_TYPES.SCHEMA];
    }

    /*
     * Return a normalize function from a map of normalizers.
     */
    public static createNormalize<NodeT extends NodeType>(
        normalizers: {
            [violation in keyof SchemaNormalizeContexts<NodeT>]?: (
                change: Change,
                context: SchemaNormalizeContextForViolation<NodeT, violation>
            ) => Change | void;
        }
    ) {
        const fn: SchemaNormalizeViolationFn<NodeT> = (change, violation, context): any => {
            return normalizers[violation] ? normalizers[violation](change, context) : undefined;
        };

        return fn;
    }

    // Properties
    public declare readonly validations: ValidateNodeFn[];

    public declare readonly document: SchemaRule;

    public declare readonly blocks: { [type: string]: SchemaRule };

    public declare readonly inlines: { [type: string]: SchemaRule };

    /*
     * Combine this schema with another one.
     */
    public combineWith(schema: Schema | Schema[]): Schema {
        if (Array.isArray(schema)) {
            return schema.reduce((r, s) => r.combineWithSchema(s), this);
        }

        return this.combineWithSchema(schema);
    }

    /**
     * Memoize version of combineWith with another immutable schema,
     * it ensures calls to Value.create with a schema are fast as it composes it with the CORE_SCHEMA.
     *
     * Since validations are memoized, having a stable schema reference is important for memoization to work.
     */
    public combineWithSchema(schema: Schema): Schema {
        return Schema.create({
            validations: [...(this.validations || []), ...(schema.validations || [])],
            document: mergeRules(this.document, schema.document),
            blocks: mergeRulesSets(this.blocks, schema.blocks),
            inlines: mergeRulesSets(this.inlines, schema.inlines),
        });
    }

    /*
     * Get the first invalid node.
     */
    public getFirstInvalidNode(node: NodeType): NodeType | null {
        if (Text.isText(node)) {
            return this.validateNode(node) ? node : null;
        }

        let result = null;

        node.nodes
            .toArray()
            .concat(Block.isBlock(node) ? node.fragments.toValuesArray() : [])
            .find((child) => {
                result = this.validateNode(child) ? child : this.getFirstInvalidNode(child);
                return result;
            });
        return result;
    }

    /*
     * Get the rule for a node.
     */
    public getRule(node: NodeType): SchemaRule | undefined {
        switch (node.object) {
            case 'document':
                return this.document;
            case 'block':
                return this.blocks[node.type];
            case 'inline':
                return this.inlines[node.type];
        }
    }

    /*
     * Get a dictionary of the parent rule validations by child type.
     */
    public getParentRules(): { [type: string]: SchemaRule } {
        const { blocks, inlines } = this;
        const parents = {};

        for (const key of Object.keys(blocks)) {
            const rule = blocks[key];
            if (rule.parent == null) {
                continue;
            }
            parents[key] = rule;
        }

        for (const key of Object.keys(inlines)) {
            const rule = inlines[key];
            if (rule.parent == null) {
                continue;
            }
            parents[key] = rule;
        }

        return Object.keys(parents).length === 0 ? null : parents;
    }

    /*
     * Fail validation by returning a normalizing change function.
     */
    public fail(
        violation: SchemaViolation,
        context: SchemaNormalizeContextForViolation<NodeType, typeof violation>
    ): SchemaNormalizeFn {
        return (change: Change) => {
            const { rule } = context;
            const { size } = change.operations;
            if (rule.normalize) {
                rule.normalize(change, violation, context);
            }
            if (change.operations.size > size) {
                return;
            }
            this.normalize(change, violation, context);
        };
    }

    /*
     * Normalize an invalid value with `violation` and `context`.
     */
    public normalize(
        change: Change,
        violation: SchemaViolation,
        context: SchemaNormalizeContext
    ): void {
        switch (violation) {
            case CHILD_OBJECT_INVALID:
            case CHILD_TYPE_INVALID:
            case CHILD_UNKNOWN:
            case FIRST_CHILD_OBJECT_INVALID:
            case FIRST_CHILD_TYPE_INVALID:
            case LAST_CHILD_OBJECT_INVALID:
            case LAST_CHILD_TYPE_INVALID: {
                const { child, node } = context;
                child.object === 'text' && node.object === 'block' && node.nodes.size === 1
                    ? change.removeNodeByKey(node.key)
                    : change.removeNodeByKey(child.key);
                return;
            }

            case CHILD_REQUIRED:
            case NODE_TEXT_INVALID:
            case PARENT_OBJECT_INVALID:
            case PARENT_TYPE_INVALID: {
                const { node, fragmentParent } = context;

                if (node.object === 'document') {
                    node.nodes.forEach((child) => change.removeNodeByKey(child.key));
                    return;
                }
                if (node.object === 'fragment') {
                    change.removeFragmentInParentByKey(fragmentParent.key, node.fragmentKey);
                    return;
                }
                change.removeNodeByKey(node.key);
                return;
            }

            case NODE_DATA_INVALID: {
                const { node, key } = context;
                node.data.get(key) === undefined && node.object !== 'document'
                    ? change.removeNodeByKey(node.key)
                    : change.removeDataPathByKey(node.key, [key]);
                return;
            }

            case NODE_IS_VOID_INVALID: {
                const { node } = context;
                change.setNodeByKey(node.key, { isVoid: !node.isVoid });
                return;
            }

            case NODE_MARK_INVALID: {
                const { node, mark } = context;
                node.getTexts().forEach((t) =>
                    change.removeMarkByKey(t.key, 0, t.text.length, mark)
                );
                return;
            }

            case NODE_UNKNOWN_FRAGMENT: {
                const { node, key } = context;
                change.removeFragmentInParentByKey(node.key, key);
                return;
            }

            case NODE_FRAGMENT_TYPE_INVALID: {
                const { node, child } = context;
                change.removeFragmentInParentByKey(node.key, child.fragmentKey);
                return;
            }

            case NODE_MISSING_FRAGMENT: {
                const { node } = context;
                change.removeNodeByKey(node.key);
                return;
            }

            case FRAGMENT_TYPE_INVALID: {
                const { node, child } = context;
                change.removeFragmentInParentByKey(node.key, child.fragmentKey);
                return;
            }

            case FRAGMENT_TYPE_MISSING: {
                const { node } = context;
                change.removeNodeByKey(node.key);
                return;
            }

            default:
                throw new Error(`Unhandled violation "${violation}"`);
        }
    }

    /*
     * Validate a `node` with the schema, returning a function that will fix the
     * invalid node, or void if the node is valid.
     */
    public validateNode(node: NodeType): SchemaNormalizeFn | undefined {
        // Run custom validations
        for (const validation of this.validations) {
            const ret = validation(node);
            if (ret != null) {
                return ret;
            }
        }

        if (node.object === 'text') {
            return;
        }

        const rule: SchemaRule = this.getRule(node) || {};
        const parents = this.getParentRules();
        const ctx: SchemaNormalizeCommonContext<NodeType> = { node, rule };

        if (typeof rule.isVoid !== 'undefined') {
            if (node.isVoid !== rule.isVoid) {
                return this.fail(NODE_IS_VOID_INVALID, ctx);
            }
        }

        if (typeof rule.data !== 'undefined') {
            for (const key of Object.keys(rule.data)) {
                const fn = rule.data[key];
                const value = node.data.get(key);

                if (!fn(value)) {
                    return this.fail(NODE_DATA_INVALID, { ...ctx, key, value });
                }
            }
        }

        if (typeof rule.marks !== 'undefined') {
            const marks = node.getMarks().toArray();

            for (const mark of marks) {
                if (!rule.marks.some((def) => def.type === mark.type)) {
                    return this.fail(NODE_MARK_INVALID, { ...ctx, mark });
                }
            }
        }

        if (typeof rule.text !== 'undefined') {
            const { text } = node;

            if (!rule.text.test(text)) {
                return this.fail(NODE_TEXT_INVALID, { ...ctx, text });
            }
        }

        if (typeof rule.nodes !== 'undefined' || parents != null) {
            const normalize = this.validateNodes(node, parents, rule.nodes, ctx);

            if (normalize) {
                return normalize;
            }
        }

        // Validate first & last nodes after validating all the nodes are valid.
        if (typeof rule.first !== 'undefined') {
            const { objects, types } = rule.first;
            const child = node.nodes.first();

            if (child && objects && !objects.includes(child.object)) {
                return this.fail(FIRST_CHILD_OBJECT_INVALID, { ...ctx, child });
            }

            if (child && types && !types.includes(child.type)) {
                return this.fail(FIRST_CHILD_TYPE_INVALID, { ...ctx, child });
            }
        }
        if (typeof rule.last !== 'undefined') {
            const { objects, types } = rule.last;
            const child = node.nodes.last();

            if (child && objects && !objects.includes(child.object)) {
                return this.fail(LAST_CHILD_OBJECT_INVALID, { ...ctx, child });
            }

            if (child && types && !types.includes(child.type)) {
                return this.fail(LAST_CHILD_TYPE_INVALID, { ...ctx, child });
            }
        }

        if (typeof rule.fragments !== 'undefined') {
            if (!Block.isBlock(node) && !Inline.isInline(node)) {
                throw new Error(`Cannot validate fragments on non-block/inline ("${node.object}")`);
            }

            // Validate the fragment keys
            if (typeof rule.fragments.keys !== 'undefined') {
                const expectedKeys = compileFragmentKeys(rule.fragments.keys, node);

                if (expectedKeys) {
                    const actualKeys = node.fragments.toKeysArray();

                    // Test that we don't have extra keys
                    const unwantedKeys = actualKeys.filter((key) => !(key in expectedKeys));
                    if (unwantedKeys.length) {
                        return this.fail(NODE_UNKNOWN_FRAGMENT, { ...ctx, key: unwantedKeys[0] });
                    }

                    // Test that we have all the expected keys
                    // and that the type is correct
                    for (const [key, expectedType] of Object.entries(expectedKeys)) {
                        const fragment = node.getFragmentByKey(key);
                        if (!fragment) {
                            return this.fail(NODE_MISSING_FRAGMENT, {
                                ...ctx,
                                key,
                            });
                        }

                        if (expectedType && fragment.type !== expectedType) {
                            return this.fail(NODE_FRAGMENT_TYPE_INVALID, {
                                ...ctx,
                                key,
                                child: fragment,
                            });
                        }
                    }
                }
            }

            // Validate the fragments by types
            const types = rule.fragments.types;
            if (typeof types !== 'undefined') {
                const nodeFragments = node.fragments.toValuesArray();
                const actualTypes: { [type: string]: number } = nodeFragments
                    .map(({ type }) => type)
                    .reduce((accu, type) => {
                        accu[`${type}`] = (accu[`${type}`] || 0) + 1;
                        return accu;
                    }, {});

                const unwantedTypes = Object.keys(actualTypes).filter((type) => !(type in types));
                if (unwantedTypes.length) {
                    return this.fail(FRAGMENT_TYPE_INVALID, {
                        ...ctx,
                        child: nodeFragments.find(({ type }) => `${type}` === unwantedTypes[0]),
                    });
                }

                // Validate that we have enough and not too many fragments of each type
                for (const [type, typeRule] of Object.entries(types)) {
                    if (typeof typeRule.min !== 'undefined') {
                        const typeNbFragments = actualTypes[type] || 0;
                        if (typeNbFragments < typeRule.min) {
                            return this.fail(FRAGMENT_TYPE_MISSING, { ...ctx, type });
                        }
                    }

                    if (typeof typeRule.max !== 'undefined') {
                        const typeNbFragments = actualTypes[type] || 0;
                        if (typeNbFragments > typeRule.max) {
                            const typeLastFragment = nodeFragments
                                .reverse()
                                .find(({ type: _type }) => `${_type}` === type);

                            return this.fail(NODE_UNKNOWN_FRAGMENT, {
                                ...ctx,
                                key: typeLastFragment.fragmentKey,
                            });
                        }
                    }
                }

                for (const fragment of node.fragments.toValuesArray()) {
                    // When reaching this point, all fragments should have a type
                    const fragmentTypeRules = rule.fragments.types[fragment.type!];
                    const fragmentRules =
                        typeof fragmentTypeRules.nodes !== 'undefined'
                            ? fragmentTypeRules.nodes
                            : rule.fragments.nodes;

                    const normalize = this.validateNodes(fragment, parents, fragmentRules, {
                        rule,
                        node: fragment,
                        fragmentParent: node,
                    });

                    if (normalize) {
                        return normalize;
                    }
                }
            }

            // Validate the fragments when not using types
            if (
                typeof rule.fragments.nodes !== 'undefined' &&
                typeof rule.fragments.types === 'undefined'
            ) {
                for (const fragment of node.fragments.toValuesArray()) {
                    const normalize = this.validateNodes(fragment, parents, rule.fragments.nodes, {
                        rule,
                        node: fragment,
                        fragmentParent: node,
                    });

                    if (normalize) {
                        return normalize;
                    }
                }
            }
        }
    }

    /**
     * Validate the children of a given `node`. Returns a normalize function,
     * or undefined if validation passes.
     */
    private validateNodes(
        node: NodeType,
        parents: { [type: string]: SchemaRule },
        nodesRule: NodesSchemaRule | undefined,
        ctx: SchemaNormalizeContext
    ): SchemaNormalizeFn | undefined {
        const children = node.nodes.toArray();
        const defs = nodesRule !== undefined ? nodesRule.slice() : [];

        let offset: number;
        let min: number;
        let index: number;
        let def:
            | NodeSchemaRule<AllowedChildObjects>
            | {
                  min?: number;
                  max?: number;
              };
        let max: number;
        let child: NodeType;

        function nextDef() {
            offset = offset == null ? null : 0;
            def = defs.shift();
            min = def && (!('min' in def) || def.min == null ? 0 : def.min);
            max = def && (!('max' in def) || def.max == null ? Number.POSITIVE_INFINITY : def.max);
            return !!def;
        }

        function nextChild() {
            index = index == null ? 0 : index + 1;
            offset = offset == null ? 0 : offset + 1;
            child = children[index];
            if (max != null && offset === max) {
                nextDef();
            }
            return !!child;
        }

        function rewind() {
            offset -= 1;
            index -= 1;
        }

        if (nodesRule !== undefined) {
            nextDef();
        }

        while (nextChild()) {
            if (parents != null && child.object !== 'text' && child.type in parents) {
                const r = parents[child.type];

                if (r.parent.objects != null && !r.parent.objects.includes(node.object)) {
                    return this.fail(PARENT_OBJECT_INVALID, {
                        node: child,
                        parent: node,
                        rule: r,
                    });
                }

                if (r.parent.types != null && !r.parent.types.includes(node.type)) {
                    return this.fail(PARENT_TYPE_INVALID, {
                        node: child,
                        parent: node,
                        rule: r,
                    });
                }
            }

            if (nodesRule !== undefined) {
                if (!def) {
                    return this.fail(CHILD_UNKNOWN, {
                        ...ctx,
                        child,
                        index,
                    });
                }

                if (
                    'objects' in def &&
                    def.objects != null &&
                    !def.objects.includes(child.object)
                ) {
                    if (offset >= min && nextDef()) {
                        rewind();
                        continue;
                    }
                    return this.fail(CHILD_OBJECT_INVALID, {
                        ...ctx,
                        child,
                        index,
                    });
                }

                if ('types' in def && def.types != null && !def.types.includes(child.type)) {
                    if (offset >= min && nextDef()) {
                        rewind();
                        continue;
                    }

                    return this.fail(CHILD_TYPE_INVALID, {
                        ...ctx,
                        child,
                        index,
                    });
                }
            }
        }

        if (nodesRule !== undefined) {
            while (min != null) {
                if (offset < min) {
                    return this.fail(CHILD_REQUIRED, { ...ctx, index });
                }

                nextDef();
            }
        }
    }
}

/**
 * Compile the set of fragment keys that should exist in the node.
 */
function compileFragmentKeys(
    input: FragmentKeysFn | FragmentKeys,
    block: Block
): { [key: string]: string | undefined } | undefined {
    const staticInput = typeof input === 'function' ? input(block) : input;

    if (Array.isArray(staticInput)) {
        return staticInput.reduce((acc, key) => {
            acc[key] = undefined;
            return acc;
        }, {});
    }

    return staticInput;
}

/*
 * Merge two schema rules together.
 */
function mergeRules(a: SchemaRule, b: SchemaRule): SchemaRule {
    const rule: SchemaRule = {};

    Object.keys(a).forEach((key) => {
        if (key in b) {
            // rule is in both a and b, merge them
            rule[key] = ruleMergeCustomizer(a[key], b[key], key as keyof SchemaRule);
        } else {
            // rule only in a
            rule[key] = a[key];
        }
    });

    Object.keys(b).forEach((key) => {
        if (!(key in a)) {
            // rule only in b
            rule[key] = b[key];
        }
    });

    return rule;
}

/*
 * Merge two schema rules together.
 */
function mergeRulesSets(a: SchemaRulesSet, b: SchemaRulesSet): SchemaRulesSet {
    const result: SchemaRulesSet = { ...a };

    for (const key in b) {
        if (Object.hasOwn(b, key)) {
            result[key] = result[key] ? mergeRules(result[key], b[key]) : b[key];
        }
    }

    return result;
}

/*
 * A Lodash customizer for merging schema rules. Special cases `objects`,
 * `marks` and `types` arrays to be unioned, and ignores new `null` values.
 * Merge two schema rules together.
 */
function ruleMergeCustomizer<K extends keyof SchemaRule>(
    target: SchemaRule[K] | undefined,
    source: SchemaRule[K] | undefined,
    key: K
): SchemaRule[K] {
    if (key === 'normalize' && source && target) {
        return (change: Change, violation: SchemaViolation, context: SchemaNormalizeContext) => {
            const original = change.operations.size;
            target(change, violation, context);

            if (change.operations.size > original) {
                return;
            }
            source(change, violation, context);
        };
    }
    if (key === 'objects' || key === 'types' || key === 'marks') {
        return target == null ? source : target.concat(source);
    }
    return source == null ? target : source;
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Schema.prototype[MODEL_TYPES.SCHEMA] = true;

/*
 * Memoize read methods.
 */

memoizeClassMethods(Schema, [
    'getParentRules',
    'getFirstInvalidNode',
    'validateNode',
    'combineWithSchema',
]);
